/* =================================================================
   operatingMetrics.js — premium "fake telemetry" without a backend.

   These helpers produce numbers that *feel* live without lying:
   - Deterministic-by-day: every agent gets a different number per
     calendar day, identical across all visits on that day, and
     naturally rolls over tomorrow. No fake jitter, no random
     spikes mid-session.
   - Count-up animation: a viewport-triggered easing from 0 to the
     target value (Linear / Stripe style "data just loaded").
   - Live elapsed timestamp: a real, ticking "Updated Xs ago" since
     the moment the card entered the viewport. The TIMER is real
     even when the COUNTER isn't.
   - Sparkline: deterministic 14-point SVG path that draws itself
     on viewport-enter (stroke-dashoffset).

   Nothing here calls a network — everything is pure client-side
   determinism keyed by today's date + agent id.
   ================================================================= */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';

/* ── deterministic primitives ───────────────────────────────────── */

function fnv1a(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function todayBucket() {
  // Day index since epoch — same value for the whole day, rolls over at 00:00 UTC.
  return Math.floor(Date.now() / 86400000);
}

function seededRand(seed) {
  let x = seed || 1;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  return ((x >>> 0) % 1000000) / 1000000;
}

export function dailySeed(key) {
  return fnv1a(`${todayBucket()}-${key}`);
}

export function dailyMetric(key, min, max) {
  const r = seededRand(dailySeed(key));
  return Math.floor(min + r * (max - min + 1));
}

export function dailySparkline(key, n = 14) {
  let s = dailySeed(key);
  const points = [];
  for (let i = 0; i < n; i++) {
    // Linear-congruential step for each subsequent point.
    s = (Math.imul(s, 1103515245) + 12345) >>> 0;
    points.push(((s % 1000) / 1000) * 0.85 + 0.075); // keep within 0.07 — 0.92
  }
  return points;
}

/* ── per-agent ranges + labels ──────────────────────────────────── */

export const AGENT_RANGES = {
  rachel: { min: 80, max: 180, en: 'conversations today',       es: 'conversaciones hoy' },
  stan:   { min: 5,  max: 18,  en: 'catering leads discovered',    es: 'leads de catering descubiertos' },
  eva:    { min: 22, max: 60,  en: 'workflows coordinated',     es: 'flujos coordinados' },
  sonny:  { min: 8,  max: 24,  en: 'campaigns scheduled',       es: 'campañas programadas' },
  penny:  { min: 1,  max: 5,   en: 'articles published',        es: 'artículos publicados' },
  linda:  { min: 3,  max: 12,  en: 'documents prepared',        es: 'documentos preparados' },
};

/* ── reactive helpers ───────────────────────────────────────────── */

// Returns a continuously-updating "Updated Xs ago" string. The clock
// starts when `startedAt` was set — pass `Date.now()` from the parent
// or use the default on first mount.
export function useElapsedLabel(startedAt, isEs = false) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = Math.max(0, Math.floor((now - (startedAt || now)) / 1000));
  if (elapsed < 60) return isEs ? `actualizado hace ${elapsed}s` : `updated ${elapsed}s ago`;
  if (elapsed < 3600) return isEs ? `actualizado hace ${Math.floor(elapsed / 60)}m` : `updated ${Math.floor(elapsed / 60)}m ago`;
  return isEs ? `actualizado hace ${Math.floor(elapsed / 3600)}h` : `updated ${Math.floor(elapsed / 3600)}h ago`;
}

/* ── visual building blocks ─────────────────────────────────────── */

// CountUp — viewport-triggered easing from 0 → target.
export function CountUp({ value, duration = 1.2, className = '', testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.floor(v));

  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(mv, value, { duration, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [inView, value, duration, mv]);

  return (
    <motion.span ref={ref} className={className} data-testid={testId}>
      {rounded}
    </motion.span>
  );
}

// Sparkline — 14-point SVG path, deterministic per agent + day, that
// draws itself on viewport-enter with a smooth stroke reveal.
export function Sparkline({
  points,
  color = '#F77F11',
  width = 64,
  height = 18,
  strokeWidth = 1.5,
  testId,
}) {
  const path = useMemo(() => {
    const n = points.length;
    return points
      .map((p, i) => {
        const x = (i / (n - 1)) * width;
        const y = height - p * height;
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ');
  }, [points, width, height]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      data-testid={testId}
      style={{ overflow: 'visible' }}
    >
      <motion.path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      />
    </svg>
  );
}

/* ── high-level composite ───────────────────────────────────────── */
/* AgentMetric — renders the full live-telemetry row for one agent.
   Three layouts: 'compact' (home OI card), 'wide' (page grid card),
   'profile' (per-agent deep section).                                */

export function AgentMetric({
  agentKey,
  isEs = false,
  color = '#F77F11',
  variant = 'compact',
  className = '',
}) {
  const range = AGENT_RANGES[agentKey];
  const value = useMemo(() => (range ? dailyMetric(agentKey, range.min, range.max) : 0), [agentKey, range]);
  const points = useMemo(() => dailySparkline(agentKey), [agentKey]);
  const startedAt = useRef(Date.now()).current;
  const elapsed = useElapsedLabel(startedAt, isEs);
  const label = range ? (isEs ? range.es : range.en) : '';

  if (variant === 'profile') {
    return (
      <div
        className={`flex items-end justify-between gap-4 ${className}`}
        data-testid={`agent-metric-${agentKey}`}
      >
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-1">
            {isEs ? 'HOY' : 'TODAY'}
          </p>
          <p className="text-3xl font-bold tracking-[-0.025em] text-neutral-900 leading-none">
            <CountUp value={value} testId={`agent-metric-count-${agentKey}`} />
          </p>
          <p className="mt-1.5 text-[12.5px] text-neutral-600">{label}</p>
          <p className="mt-1 text-[10.5px] tracking-[0.04em] text-neutral-400">
            {elapsed}
          </p>
        </div>
        <div className="shrink-0 pb-1">
          <Sparkline points={points} color={color} width={96} height={28} testId={`agent-sparkline-${agentKey}`} />
        </div>
      </div>
    );
  }

  if (variant === 'wide') {
    return (
      <div
        className={`flex items-center justify-between gap-3 ${className}`}
        data-testid={`agent-metric-${agentKey}`}
      >
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-neutral-400">
            {isEs ? 'HOY' : 'TODAY'}
          </p>
          <p className="mt-0.5 text-xl font-bold tracking-[-0.015em] text-neutral-900">
            <CountUp value={value} testId={`agent-metric-count-${agentKey}`} />{' '}
            <span className="text-[12px] font-medium text-neutral-500">{label}</span>
          </p>
          <p className="mt-0.5 text-[10px] tracking-[0.04em] text-neutral-400">{elapsed}</p>
        </div>
        <Sparkline points={points} color={color} width={72} height={22} testId={`agent-sparkline-${agentKey}`} />
      </div>
    );
  }

  // Default compact — fits inside the dark home OI agent card.
  return (
    <div
      className={`flex items-center justify-between gap-2 ${className}`}
      data-testid={`agent-metric-${agentKey}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-neutral-500 leading-tight">
          <span className="text-white font-bold text-[14px] mr-1">
            <CountUp value={value} testId={`agent-metric-count-${agentKey}`} />
          </span>
          {label}
        </p>
        <p className="mt-0.5 text-[9.5px] tracking-[0.04em] text-neutral-500">{elapsed}</p>
      </div>
      <Sparkline points={points} color={color} width={56} height={18} testId={`agent-sparkline-${agentKey}`} />
    </div>
  );
}
